// shared/format.ts: small pure text helpers used by both the SPA and the Worker.

// C0 and C1 control characters (CR/LF included), DEL, Unicode line and paragraph separators, and bidi
// embedding/override/isolate characters, which can disguise text in subjects and names.
const UNPRINTABLE = /[\u0000-\u001F\u007F-\u009F\u2028\u2029\u202A-\u202E\u2066-\u2069]/g;

/**
 * One printable line: control characters (CR/LF included) become spaces, whitespace collapses,
 * and the result is trimmed and cut to `max` characters. Used for names, relation notes, firms and
 * email subject values (spec 5.4, 8.3).
 */
export function cleanLine(value: string, max = 120): string {
  const s = value.replace(UNPRINTABLE, " ").replace(/\s+/g, " ").trim();
  return Array.from(s).slice(0, max).join("").trim();
}

/** Trimmed and lowercased; the form every users.email is stored in. */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

// Written so the SPA bundle's source maps never look like they carry an address (scripts/check-dist.ts).
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+[.][^\s@]{2,}$/;

/** A deliberately simple syntax check (one @, a dot in the domain, no spaces). */
export function isEmail(email: string): boolean {
  return email.length <= 254 && EMAIL_SHAPE.test(email);
}

/** Keeps the first character and the domain, and masks the rest of the local part with three dots. */
export function maskEmail(email: string): string {
  const at = email.lastIndexOf("@");
  if (at <= 0) return "\u2022\u2022\u2022";
  const first = Array.from(email)[0] ?? "";
  return `${first}\u2022\u2022\u2022${email.slice(at)}`;
}

/** "Dana Whitaker" becomes "Dana"; an empty name stays empty. */
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? "";
}

/** "Jr.", "Sr", "II", "III", "IV" (with or without a trailing period or comma). */
const NAME_SUFFIX = /^(?:jr|sr|ii|iii|iv)[.,]?$/i;

/** Words that join two names and are never an initial: "&", "+", "and" ("Harbor & Pine", "Dana and Chris"). */
const CONNECTOR = /^(?:&|\+|and)$/i;

/** The words of a name that can give an initial: they start with a letter and are not connectors. */
function initialWords(name: string): string[] {
  return name
    .trim()
    .split(/\s+/)
    .filter((w) => /^\p{L}/u.test(w) && !CONNECTOR.test(w));
}

/**
 * A builder's or a team's monogram: the first letters of its first two words, skipping connectors and anything that
 * doesn't start with a letter, so "Apex Builders Group" is "AB" and "Harbor & Pine Homes" is "HP"; empty gives "?".
 */
export function monogram(name: string): string {
  const letters = initialWords(name)
    .slice(0, 2)
    .map((w) => (Array.from(w)[0] ?? "").toUpperCase());
  return letters.join("") || "?";
}

/**
 * "Dana Whitaker" becomes "DW"; one word gives one letter; empty gives "?". Only words that start with a letter
 * count, so "Taylor Brooks (demo)" is "TB", not "T(", a suffix is skipped, so "Mark DeAngelis, Jr." is "MD", and so
 * is a connector, so "Dana and Chris" is "DC".
 */
export function initials(name: string): string {
  const all = name.trim().split(/\s+/).filter(Boolean);
  const words = all.filter((w, i) => /^\p{L}/u.test(w) && !CONNECTOR.test(w) && !(i > 0 && NAME_SUFFIX.test(w)));
  const parts = words.length > 0 ? words : all;
  if (parts.length === 0) return "?";
  const firstChar = (s: string) => (Array.from(s)[0] ?? "").toUpperCase();
  const first = parts[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1] ?? "") : "";
  return firstChar(first) + (last ? firstChar(last) : "");
}

/** The parts that exist, joined: no empty piece and no doubled or dangling separator ("Cedar Row · · phone"). */
export function joinParts(parts: ReadonlyArray<string | null | undefined | false>, sep = " \u00b7 "): string {
  return parts
    .map((p) => (typeof p === "string" ? p.trim() : ""))
    .filter(Boolean)
    .join(sep);
}

type HomeSpecs = { beds: number | null; baths: number | null; sqft: number | null };

/**
 * A home's numbers as people read them: "4 bd · 3.5 ba · 3,600 sq ft" (short) or "4 beds · 3.5 baths · 3,600 sq ft".
 * Unknown shows nothing, never 0: null and 0 are both "not given" (a custom home has bedrooms, and the setup form
 * has always stored a blank as 0).
 */
export function homeSpecsLine(s: HomeSpecs, short = false): string {
  const beds = s.beds && s.beds > 0 ? (short ? `${s.beds} bd` : `${s.beds} ${s.beds === 1 ? "bed" : "beds"}`) : "";
  const baths = s.baths && s.baths > 0 ? (short ? `${s.baths} ba` : `${s.baths} ${s.baths === 1 ? "bath" : "baths"}`) : "";
  const sqft = s.sqft && s.sqft > 0 ? `${s.sqft.toLocaleString("en-US")} sq ft` : "";
  return joinParts([beds, baths, sqft]);
}
